/**
 * Parses the Avis app's product-page payload (feature 0066 lot E).
 *
 * Defensive on purpose, like `parseProductCountdown`: this crosses a network boundary, and a malformed
 * payload must make the reviews ABSENT, never make the product page throw. A shop losing its review block
 * for one render is a disappointment; a shop losing its product page is an outage.
 */
export function parseProductReviews(raw) {
    if (!isRecord(raw))
        return undefined;
    const count = int(raw['count']);
    if (count <= 0)
        return undefined;
    const items = Array.isArray(raw['items'])
        ? raw['items'].map(parseReview).filter((r) => r !== null)
        : [];
    return {
        count,
        average: num(raw['average']),
        histogram: parseHistogram(raw['histogram']),
        photoCount: int(raw['photoCount']),
        items,
    };
}
function parseReview(raw) {
    if (!isRecord(raw))
        return null;
    const body = str(raw['body']).trim();
    const rating = num(raw['rating']);
    // A review is its STARS, not its words.
    //
    // This used to drop every review with an empty body, and the backend importer had already decided the
    // opposite ("a star with no words IS a review" — refusing them threw away the bulk of every real file).
    // The two halves disagreeing is what made a product page with 1 535 imported reviews render NOTHING:
    // 1 495 of them are ratings without text, the ten the page carries were all of that kind, they were all
    // dropped here, and the section then removed itself (founder, 16/09/2026). What is not a review is a row
    // with no rating at all — that is a malformed payload, and it is still refused.
    if (rating <= 0)
        return null;
    return {
        id: str(raw['id']),
        rating,
        title: str(raw['title']),
        body,
        authorName: str(raw['authorName']),
        authorCountry: str(raw['authorCountry']),
        date: str(raw['date']),
        // Anything other than a literal true is false: a badge that asserts a purchase does not get coerced
        // into existence by a truthy string.
        verified: raw['verified'] === true,
        // A photo is something the page puts in an `<img src>`. The backend refuses to STORE anything that is
        // not http(s) since the QA pass; this is the second gate, because the payload crosses a network and
        // the page must not depend on the other end having been correct.
        photos: Array.isArray(raw['photos']) ? raw['photos'].map(str).filter(isWebUrl) : [],
        reply: typeof raw['reply'] === 'string' && raw['reply'].trim() !== '' ? raw['reply'] : null,
        source: str(raw['source']),
        // An older backend does not send it; zero is the honest reading of "nobody has pressed it here".
        helpfulCount: int(raw['helpfulCount']),
    };
}
/** Always five bars, 1..5 — a missing bar is a hole in the chart, not an absent one. */
function parseHistogram(raw) {
    const out = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
    if (isRecord(raw)) {
        for (const star of ['1', '2', '3', '4', '5'])
            out[star] = int(raw[star]);
    }
    return out;
}
/** http(s) only, and short enough to be an address rather than a payload. */
function isWebUrl(value) {
    return value.length <= 2000 && (value.startsWith('https://') || value.startsWith('http://'));
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function str(value) {
    return typeof value === 'string' ? value : '';
}
function num(value) {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}
function int(value) {
    return Math.max(0, Math.trunc(num(value)));
}
//# sourceMappingURL=reviewsPayload.js.map